import {ClockRegularIcon} from 'shells/icon/svg/clock-regular-icon';
import {InfoSquareIcon} from 'shells/icon/svg/info-square-icon';
import {LeadChevronRegularIcon} from 'shells/icon/svg/lead-chevron-regular-icon';
import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {TagRegularDiagonalIcon} from 'shells/icon/svg/tag-regular-diagonal-icon';
import {TextRegularAiIcon} from 'shells/icon/svg/text-regular-ai-icon';

import {appHistory} from '../../../history';

import {
    LEAD_RESEARCH_AGENT,
    EMAIL_SEQUENCE_AGENT,
    SALES_OBJECTION_AGENT,
    SALES_PROCESS_AGENT,
} from '../../team-ai/helpers';

import {AI_FEATURES} from './features-list';
import type {AiFeature, AiFeatureGrouping, TryItFeature} from './types';
import {TRY_IT_FEATURES} from './types';

/**
 * A key value pair where each key is a grouping and each value is an array of
 * features in that grouping
 *
 * @returns {{[AiFeatureGrouping]: Array<AiFeature>}}
 */
export function getAiFeaturesGrouped(): Partial<Record<AiFeatureGrouping, Array<AiFeature>>> {
    return AI_FEATURES.reduce<Record<string, any>>((acc, feature) => {
        acc[feature.grouping] = acc[feature.grouping] || [];
        acc[feature.grouping].push(feature);

        return acc;
    }, {});
}

export function getAiFeatureGroupingName(grouping: AiFeatureGrouping): string {
    switch (grouping) {
        case 'assistants':
            return 'Workflow assistants';
        case 'salesAcceleration':
            return 'Sales acceleration';
        case 'notetakers':
            return 'Meeting and phone notetakers';
        case 'marketing':
            return 'Marketing and Lead generation';
        case 'reporting':
            return 'Reporting and reminders';
        case 'other':
        default:
            return 'Other';
    }
}

export function getFeatureGroupingTabLabel(grouping: AiFeatureGrouping): string {
    switch (grouping) {
        case 'assistants':
            return 'Assistants';
        case 'salesAcceleration':
            return 'Sales';
        case 'notetakers':
            return 'Notetakers';
        case 'marketing':
            return 'Lead generation';
        case 'reporting':
            return 'Reporting';
        case 'other':
        default:
            return 'Other';
    }
}

export function getAiFeatureGroupingIcon(grouping: AiFeatureGrouping): React.ComponentType<any> {
    switch (grouping) {
        case 'assistants':
            return TextRegularAiIcon;
        case 'salesAcceleration':
            return TagRegularDiagonalIcon;
        case 'notetakers':
            return NoteRegularIcon;
        case 'marketing':
            return LeadChevronRegularIcon;
        case 'reporting':
            return ClockRegularIcon;
        case 'other':
        default:
            return InfoSquareIcon;
    }
}

/**
 * Checks if a feature type is supporting a "Try it now" action
 *
 * @param {string} featureType - The type of the AI feature
 * @returns {boolean} - True if the feature type has an implementation
 */
export function isTryItFeature(featureType: string): boolean {
    // @ts-expect-error
    return TRY_IT_FEATURES.includes(featureType);
}

/**
 * Handles the "Try it now" action for a given feature type. For agent-based
 * features (LEAD_RESEARCHER, EMAIL_SEQUENCE_WRITER, SALES_OBJECTION_COACH,
 * SALES_PIPELINE_DESIGNER), it dispatches a CustomEvent to open the agent
 * sidebar. For all other features, it routes to the feature's page.
 *
 * @param {TryItFeature} featureType - The type of the AI feature
 * @param {(show: boolean) => void} setShowEmailCampaignBuilder - A function to set whether to show the email campaign builder modal
 */
export function handleTryItFeature(
    featureType: TryItFeature,
    setShowEmailCampaignBuilder: (show: boolean) => void
): void {
    let agentKey = null;
    switch (featureType) {
        case 'LEAD_RESEARCHER':
            agentKey = LEAD_RESEARCH_AGENT;
            break;
        case 'EMAIL_SEQUENCE_WRITER':
            agentKey = EMAIL_SEQUENCE_AGENT;
            break;
        case 'SALES_OBJECTION_COACH':
            agentKey = SALES_OBJECTION_AGENT;
            break;
        case 'SALES_PIPELINE_DESIGNER':
            agentKey = SALES_PROCESS_AGENT;
            break;
        default:
            break;
    }

    if (agentKey) {
        // Dispatch the event - the sidebar will handle opening itself and
        // setting the agent
        window.dispatchEvent(
            new CustomEvent('teamAiOpenAgent', {
                detail: {agentKey},
            })
        );
    } else {
        switch (featureType) {
            case 'GOOGLE_MEET_NOTETAKER':
                appHistory.push('/sales/meetings/meet');
                break;
            case 'ZOOM_NOTETAKER':
                appHistory.push('/sales/meetings/zoom');
                break;
            case 'MICROSOFT_TEAMS_NOTETAKER':
                appHistory.push('/sales/meetings/teams');
                break;
            case 'CLICK_TO_CALL_NOTETAKER':
            case 'RINGCENTRAL_NOTETAKER':
            case 'AIRCALL_NOTETAKER':
            case 'DIALPAD_NOTETAKER':
            case 'CALLRAIL_NOTETAKER':
            case 'KIXIE_NOTETAKER':
                appHistory.push('/sales/calls');
                break;
            case 'COMPANY_IQ':
                appHistory.push('/setup/people-iq');
                break;
            case 'PEOPLE_IQ':
                appHistory.push('/setup/people-iq');
                break;
            case 'VISITOR_IQ':
                appHistory.push('/marketing/visitors/list');
                break;
            case 'PROSPECTOR_IQ':
                appHistory.push('/sales/prospectoriq');
                break;
            case 'COMPETITOR_SPY':
                appHistory.push('/marketing/competitors/charts');
                break;
            case 'EMAIL_CAMPAIGN_BUILDER':
                setShowEmailCampaignBuilder(true);
                break;
            case 'SALES_CHATBOT':
                appHistory.push('/inbox/settings/chat');
                break;
            case 'AI_FOLLOW_UP_TASKS':
                appHistory.push('/setup/ai-features');
                break;
            default:
                break;
        }
    }
}
